import { IFilterService, IPromise, IQService, IScope, ITimeoutService } from "angular";
import { StateService, Transition, TransitionService, UIRouterGlobals } from "@uirouter/angularjs";
import { Observable, first, take, takeUntil } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { generateDefaultMandatoryOkrAlignmentAccountSettings } from "@gtmhub/configuration/utils/utils";
import { IRestLayerRequest, RestLayerFilter } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { CustomFieldActions } from "@gtmhub/customFields/redux/custom-field-actions";
import { ICustomFieldsStoreState } from "@gtmhub/customFields/redux/custom-field-reducer";
import { DocumentExporterService } from "@gtmhub/document-exporter/document-exporter-service";
import { CurrentEmployeeActions } from "@gtmhub/employees";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { expandedGoalsScreenPath, extractArrayFilterValues, filterFieldNames } from "@gtmhub/goals/filter-utils";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { resetPersistedExpandedItems } from "@gtmhub/okrs/alignment-tree/utils";
import { IMetricChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { formatAttainmentProgressValue } from "@gtmhub/okrs/metrics/utils";
import { navigateToSingleOkrView } from "@gtmhub/okrs/okr-views/utils/utils";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { PlanningSessionEvents } from "@gtmhub/sessions/services/events";
import {
  GridColumnDefinitions,
  GridFilterDefinitions,
  GridFilterOperator,
  GridSortingDefinitions,
  IArrayGridFilter,
  IGridColumn,
  IGridFiltersChange,
  IGridSortingRule,
  OrFiltersGroup,
  RestLayerFilterFactory,
  createFilter,
  getPersistedScreenSettings,
  removeFiltersByFieldNameAndEmptyGroups,
  setPersistedScreenSettings,
  toGridDefinitionMap,
  toRestLayerRequest,
} from "@gtmhub/shared/components/grid-filtering-bar";
import {
  addValueToExistingFiltersOrCreateNewFilterInEveryGroup,
  getCommonValuesUsedInAllFiltersInEveryGroup,
  hasFiltersGroupsWithValues,
  removeValueFromFiltersInEveryGroup,
} from "@gtmhub/shared/components/grid-filtering-bar/grid-filters/util";
import { Ng1UiModalService } from "@gtmhub/shared/components/modal/ng1-modal.service";
import { SimplePermissionsAccessType } from "@gtmhub/shared/components/simple-permissions/simple-permissions.component";
import { determineAccessType } from "@gtmhub/shared/components/simple-permissions/simple-permissions.utils";
import { triggerFileDownload } from "@gtmhub/shared/dom";
import { separateDynamicValuesForAllGroups } from "@gtmhub/shared/filters/separate-dynamic-filters-util";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { scrollToTop } from "@gtmhub/shared/utils/dom-utils";
import { IGoalsMetricsData } from "@gtmhub/sockets";
import { INgRedux } from "@gtmhub/state-management";
import { TeamsActions } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { IdMap, NameMap, toKeyMap } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { IMandatoryOkrAlignmentSettings } from "@webapp/configuration/models/configuration.model";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { NavigationEvents } from "@webapp/navigation/components/navigation/navigation.events";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { MetricMilestoneEventType } from "@webapp/okrs/metrics/models/metric.models";
import { OkrViewEventsManager } from "@webapp/okrs/okr-views/utils/okr-view.events";
import { getDisplayableOwnerIds } from "@webapp/okrs/utils/utils";
import { GuidedOKRsComponent, GuidedOKRsModalData } from "@webapp/platform-intelligence/guided-okrs/guided-okrs.component";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { IAllowedActionsMap, IUserItemAllowedActions, Session, UserAction } from "@webapp/sessions/models/sessions.model";
import { SaveFilterChangesContent } from "@webapp/shared/unified-filters/components/save-filter-changes/save-filter-changes.component";
import { ShareableFilter, ShareableFilterBasePostDTO, ShareableFilterTransientPostDTO } from "@webapp/shared/unified-filters/models/shareable-filters.models";
import { ShareableFiltersModalsService } from "@webapp/shared/unified-filters/services/shareable-filters-modals.service";
import { ShareableFiltersFacade } from "@webapp/shared/unified-filters/services/shareable-filters/shareable-filters-facade.service";
import { FULL_NUMBER_PRECISION } from "@webapp/shared/unit-selector/models/unit-selector.models";
import { ITeamsSelectedEventArgs, TeamEvents } from "../../teams/events";
import { OkrMethodologyFacade } from "../components/facades/okr-methodology.facade";
import {
  filtersOverrideForOkrName,
  filtersOverrideForSessionlessGoals,
  filtersOverrideForTagTitle,
  generateDefaultFilters,
  goalsFilterDefinitions,
  goalsGridPersistedScreenPath,
  goalsPersistedScreenPath,
  goalsViewOptionsPath,
  hasOnlyHostSessionFilterInAllGroups,
  processedFiltersForSessionlessGoals,
} from "../filter-utils";
import {
  IAllGoal,
  IAllGoalTag,
  IAllMetric,
  IGoal,
  IOkrGridColumn,
  MISSING_SESSION_ID,
  MISSING_SESSION_ID_STATE_PARAM,
  OkrGridContributor,
  OkrMethodologySettings,
} from "../models";
import { GoalService } from "../services/goal.service";
import { GoalsTreeService } from "../services/goals-tree.service";
import {
  buildParentChildrenIdMap,
  convertColumnToColumnDefinition,
  convertColumnToSortingDefinition,
  generateDefaultOkrMethodologySettings,
  getOkrGridColumns,
} from "../utils";

const PAGING_SIZE = 20;
const FIRST_PAGE = 1;

interface IAllGoalsBindings {
  filtersUpdated(filters: OrFiltersGroup[]): void;
}

interface IBulkDeleteGoalIdCollection {
  items: string[];
  count: number;
}

const bindStateToScope = (state: ISessionsStoreState & ICustomFieldsStoreState) => {
  const { map: sessionsIdMap, items: allSessions, isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { map: customFields, isFetched: customFieldsFetched, error: customFieldsError } = state.customFields;

  return {
    allSessions,
    sessionsIdMap,
    sessionsFetched,
    sessionsError,
    customFields,
    customFieldsFetched,
    customFieldsError,
    customFieldsNameMap: customFields?.goal?.reduce((map, customFieldDefinition) => ({ ...map, [customFieldDefinition.name]: customFieldDefinition }), {}),
  };
};
export interface IAllGoalsScope extends ReturnType<typeof bindStateToScope>, IScope, IExtendedRootScopeService, IAllGoalsBindings {
  isModuleTagsEnabled: boolean;
  isOkrAlignmentMandatory: boolean;
  isOkrFiltersImprovementEnabled: boolean;
  isShareableLinksWithAppliedFiltersFeatureEnabled: boolean;
  isOkrViewsFeatureEnabled: boolean;
  isGuidedOKRsCreationEnabled: boolean;
  isOkrViewDisplayed: boolean;
  hasFilterOrSortingChanged: boolean;

  session: Session;
  goals: IAllGoal[];
  ownersMap: Record<string, string[]>;
  indicators: {
    loading?: IIndicator;
    reloading?: boolean;
  };
  date: Date;
  workflowEnabled: boolean;

  allowedActionsMap: Map<string, Set<UserAction>>;
  showActionsDropDown: boolean;
  showAssignActionButton: boolean;
  showTagActionButton: boolean;
  showDeleteActionButton: boolean;
  showDeleteWarningBanner: boolean;
  isGoalDeletable(goal: IGoal): boolean;

  columns: IOkrGridColumn[];
  filterDefinitions: GridFilterDefinitions;
  filters: OrFiltersGroup[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  columnDefinitions: GridColumnDefinitions;
  gridColumns: IGridColumn[];
  filterTagSelection: Set<string>;
  totalPages: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange(page: number): void;

  showEmptyBlock: boolean;
  showNoFilterResults: boolean;
  parentChildrenIdMap: IdMap<OkrGridContributor[]>;
  methodologySettings: OkrMethodologySettings;
  saveFilterChangesContent: SaveFilterChangesContent;

  goalsBulkAction: "delete";
  toggleGoalsBulkAction(action: IAllGoalsScope["goalsBulkAction"]): void;
  goalsIdsToBulkDelete: IBulkDeleteGoalIdCollection;
  toggleGoalIdToBulkDelete(goalId: string): void;
  toggleAllGoalsIdsToBulkDelete(args: { checked: boolean }): void;
  handleGoalsDeleted(): void;
  deletableGoalsCount: number;

  isProgressAggregationDisabledForSession(sessionId: string): boolean;
  filtersChange(change: IGridFiltersChange): void;
  sortingChange(): void;
  columnsChange(): void;
  filterByTag(tag: string): void;
  tag(item: IAllGoalTag, subject: IAllGoal): void;
  removeTag(goal: IAllGoal, tag: IAllGoalTag): void;
  openGoal(id: string): void;
  deleteGoal(id: string): void;
  deleteMetric(goalId: string, metricId: string): void;
  deleteContributor(contributor: OkrGridContributor, goal: IAllGoal): void;
  changeAssignee(item: Assignee, subject: IAllGoal | IAllMetric, subjectType: string): void;
  exportToExcel(): void;
  copyShareableLinkWithAppliedFilters(): void;
  formatAttainmentProgressValue(progress: number): number;
  openQuantivePlusWizard(): void;
  openGoalForm(): void;
  customNumericFieldHasPrecision(field: ICustomField): boolean;
  openOkrViewCreationModal(): void;
}

export interface IAllGoalsStateParams {
  page?: string;
  planningSessionId?: string;
  filterId?: string;
  okrViewId?: string;
}

const gridScreenName = "grid";

export class AllGoalsCtrl implements IStateInit {
  private teamEvents: TeamEvents;
  private okrEvents: OkrEvents;
  private sessionEvents: PlanningSessionEvents;
  private columnsFieldMap: NameMap<IOkrGridColumn>;
  private goalsTotalCount: number;
  private gridFiltersChange: IGridFiltersChange;
  private isFirstLoad = true;
  private sharedFilterSettings: ShareableFilter;
  private okrViewEventsManager: OkrViewEventsManager;
  private destroy$: Observable<unknown>;
  private currentUserAllowedActionsMap: IAllowedActionsMap;

  public static $inject = [
    "$q",
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$timeout",
    "GoalService",
    "GoalsTreeService",
    "MetricService",
    "PermissionsService",
    "DocumentExporterService",
    "UIErrorHandlingService",
    "CustomFieldActions",
    "$ngRedux",
    "PlanningSessionsActions",
    "CurrentEmployeeActions",
    "TeamsActions",
    "$filter",
    "FeatureTogglesFacade",
    "OkrMethodologyFacade",
    "AccountService",
    "AnalyticsService",
    "ApmSpanService",
    "$uiRouterGlobals",
    "ShareableFiltersFacade",
    "Ng1UiModalService",
    "PiFeatureVisibilityFacade",
    "$transitions",
    "ShareableFiltersModalsService",
  ];

  constructor(
    private $q: IQService,
    private $rootScope: ITraceRootScopeService & IGtmhubRootScopeService,
    private $scope: IAllGoalsScope & IExtendedRootScopeService,
    private $state: StateService,
    private $stateParams: IAllGoalsStateParams,
    private $timeout: ITimeoutService,
    private goalService: GoalService,
    private goalsTreeService: GoalsTreeService,
    private metricService: MetricService,
    private permissionsService: PermissionsService,
    private documentExporterService: DocumentExporterService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private customFieldsActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private currentEmployeeActions: CurrentEmployeeActions,
    private teamsActions: TeamsActions,
    private $filter: IFilterService,
    private featureToggleService: FeatureTogglesFacade,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private accountService: AccountService,
    private analyticsService: AnalyticsService,
    private apmSpanService: ApmSpanService,
    private $uiRouterGlobals: UIRouterGlobals,
    private shareableFiltersFacade: ShareableFiltersFacade,
    private ng1UiModalService: Ng1UiModalService,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private $transitions: TransitionService,
    private shareableFiltersModalsService: ShareableFiltersModalsService
  ) {
    this.$rootScope.noNavigation = false;
    this.teamEvents = new TeamEvents(this.$scope);
    this.okrEvents = new OkrEvents(this.$scope);
    this.sessionEvents = new PlanningSessionEvents(this.$scope);
    this.destroy$ = new Ng1DestroyService(this.$scope);
  }

  public stateInit(): IPromise<unknown> {
    // set to an empty array to override the value that is present in $rootScope after visiting an insight
    // until the data is fetched and the proper intialization is conducted
    this.$scope.filters = undefined;
    this.$scope.formatAttainmentProgressValue = formatAttainmentProgressValue;

    this.$scope.isOkrViewDisplayed = this.$stateParams.okrViewId !== undefined;

    this.$scope.indicators = { loading: { progress: true } };

    this.setDefaultMethodologySettings();

    this.$scope.$on("tagsChanged", () => this.getGoals());

    const userPermissionsListener = this.sessionEvents.onSessionUpdated(() => {
      this.loadAllowedActionsMap(this.getDisplayedSessionIds());
    });
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", () => {
      unsubscribe();
      userPermissionsListener();
    });
    this.$scope.$on(NavigationEvents.SUB_NAVIGATION_ITEM_SHAREABLE_LINK_COPIED_TO_CLIPBOARD, this.handleShareableLinkCopiedFromSubNavigation);

    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.$ngRedux.dispatch(this.customFieldsActions.getCustomFields());
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

    this.piFeatureVisibilityFacade
      .isGuidedOKRsCreationVisible$()
      .pipe(take(1))
      .subscribe((enabled) => (this.$scope.isGuidedOKRsCreationEnabled = enabled));

    return this.apmSpanService.dataLoadSpan("custom-fields-feature-flags", () =>
      this.$q
        .all({
          _: this.$scope.watchUntilAndRejectOnError(
            () => this.$scope.customFieldsFetched,
            () => this.$scope.customFieldsError
          ),
          isModuleTagsEnabled: this.featureToggleService.isFeatureAvailable(ModuleFlag.Tags),
          isMandatoryOkrAlignmentEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.MandatoryOkrAlignmentFeature),
          isOkrFiltersImprovementEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.OkrFiltersImprovement),
          isShareableLinksWithAppliedFiltersFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ShareableLinksWithAppliedFilters),
          isOkrViewsFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.OkrViews),
        })
        .then(
          ({
            isModuleTagsEnabled,
            isMandatoryOkrAlignmentEnabled,
            isOkrFiltersImprovementEnabled,
            isShareableLinksWithAppliedFiltersFeatureEnabled,
            isOkrViewsFeatureEnabled,
          }) => {
            this.$scope.isModuleTagsEnabled = isModuleTagsEnabled;
            this.$scope.isOkrFiltersImprovementEnabled = isOkrFiltersImprovementEnabled;
            this.$scope.isShareableLinksWithAppliedFiltersFeatureEnabled = isShareableLinksWithAppliedFiltersFeatureEnabled;
            this.$scope.isOkrViewsFeatureEnabled = isOkrViewsFeatureEnabled;

            const mandatoryOkrAlignmentSettings: IMandatoryOkrAlignmentSettings =
              this.accountService.getAccountSetting("mandatoryOkrAlignment") || generateDefaultMandatoryOkrAlignmentAccountSettings();
            this.$scope.isOkrAlignmentMandatory =
              isMandatoryOkrAlignmentEnabled && mandatoryOkrAlignmentSettings.enabled && !mandatoryOkrAlignmentSettings.excludedUserIds.includes(getCurrentUserId());

            return this.init();
          },
          (error) => {
            this.catchError(error);
          }
        )
        .finally(() => {
          this.isFirstLoad = false;
        })
    );
  }

  private init = (): IPromise<unknown> => {
    this.setColumnsAndSortingDefinitions();
    this.resetGoalsIdsToBulkDelete();
    this.$scope.filterDefinitions = goalsFilterDefinitions({
      customFields: this.$scope.customFields,
      planningSessionId: this.$stateParams.planningSessionId,
      isNestedView: null,
    });

    this.$scope.itemsPerPage = PAGING_SIZE;
    this.$scope.onPageChange = this.onPageChange;
    this.watchForPageChanges();
    this.checkMethodologySettings();

    const paramsPage = Number(this.$stateParams.page);
    this.$scope.currentPage = isNaN(paramsPage) ? FIRST_PAGE : paramsPage;

    this.$scope.filterTagSelection = new Set();
    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.columnsChange = this.columnsChange;
    this.$scope.exportToExcel = this.exportToExcel;

    this.$scope.removeTag = this.removeTag;

    this.$scope.changeAssignee = this.changeAssignee;
    this.$scope.tag = this.tag;

    this.$scope.deleteGoal = this.deleteGoal;
    this.$scope.deleteMetric = this.deleteMetric;
    this.$scope.deleteContributor = this.deleteContributor;
    this.$scope.toggleGoalsBulkAction = this.toggleGoalsBulkAction;
    this.$scope.toggleGoalIdToBulkDelete = this.toggleGoalIdToBulkDelete;
    this.$scope.toggleAllGoalsIdsToBulkDelete = this.toggleAllGoalsIdsToBulkDelete;
    this.$scope.handleGoalsDeleted = this.handleGoalsDeleted;

    this.$scope.filterByTag = this.filterByTag;
    this.$scope.openGoal = this.openGoal;
    this.$scope.isProgressAggregationDisabledForSession = this.isProgressAggregationDisabledForSession;

    this.teamEvents.onTeamsSelected((args) => this.onTeamsSelected(args));
    this.okrEvents.onGoalsChanged((args) => args.reason === "goalChildReordered" && this.onGoalsChanged());
    this.okrEvents.onGoalsChanged((args) => args.reason === "metricCreated" && this.onMetricCreated(args.detail));
    this.$scope.$on("goalsChanged", this.onGoalsChanged);
    this.$scope.$on("goalDeleted", this.onGoalDeleted);
    this.$scope.$on("gridColumnsChange", this.setColumnsAndSortingDefinitions);

    this.$scope.date = new Date();
    this.$scope.$on("wsGoalsAndMetricsChanged", this.updateGoalsTree);

    this.$scope.isGoalDeletable = this.isGoalDeletable;
    this.$scope.copyShareableLinkWithAppliedFilters = this.copyShareableLinkWithAppliedFilters;

    this.$scope.openQuantivePlusWizard = this.openQuantivePlusWizard;
    this.$scope.openGoalForm = this.openGoalForm;
    this.$scope.customNumericFieldHasPrecision = this.customNumericFieldHasPrecision;
    this.$scope.openOkrViewCreationModal = this.openOkrViewCreationModal;

    this.setSessionData();

    return this.initGridFilters().then(() => {
      return this.getGoals();
    });
  };

  private loadAllowedActionsMap(sessionIds: string[]): IPromise<void> {
    // disallow all actions until the corresponding permissions are fetched
    this.$scope.allowedActionsMap?.clear();
    this.$scope.showActionsDropDown = false;

    // avoids sending an allowed-actions query for the dummy `000000000000000000000000` non-existing session ID
    const validSessionIds = sessionIds.filter((sessionId) => sessionId !== MISSING_SESSION_ID);

    const loadAllowedActions = () => {
      const allowedActionsPromise =
        validSessionIds.length > 0 ? this.permissionsService.getCurrentUserAllowedActionsForItems(validSessionIds, "session") : this.$q.resolve({});

      return allowedActionsPromise
        .then((actionsMap) => ({ ...actionsMap, [MISSING_SESSION_ID]: ["delete", "update"] })) //  allows delete/update on sessionless objectives
        .then((actionsMap) => {
          // convert the `{ [sessionId]: string[] }` object to a Map of Sets => checks for each okr will be made from the template and Sets have quicker look-up time
          this.$scope.allowedActionsMap = Object.keys(actionsMap).reduce((map, key) => map.set(key, new Set(actionsMap[key])), new Map());

          this.$scope.showDeleteActionButton = sessionIds.some(
            (sessionId) =>
              sessionId === MISSING_SESSION_ID || (this.$scope.sessionsIdMap[sessionId]?.status === "open" && this.$scope.allowedActionsMap.get(sessionId)?.has("delete"))
          );

          this.$scope.showDeleteWarningBanner = sessionIds.some(
            (sessionId) =>
              sessionId !== MISSING_SESSION_ID &&
              (this.$scope.sessionsIdMap[sessionId]?.status !== "open" || !this.$scope.allowedActionsMap.get(sessionId)?.has("delete"))
          );

          // TODO: revisit the following two after design feedback - some sessions might disallow 'update'
          // https://gtmhub.atlassian.net/browse/GVS-24775
          this.$scope.showAssignActionButton = sessionIds.some(
            (sessionId) => sessionId === MISSING_SESSION_ID || this.$scope.allowedActionsMap.get(sessionId)?.has("update")
          );

          this.$scope.showTagActionButton =
            this.$scope.isModuleTagsEnabled &&
            sessionIds.some((sessionId) => sessionId === MISSING_SESSION_ID || this.$scope.allowedActionsMap.get(sessionId)?.has("update"));

          this.$scope.showActionsDropDown = this.$scope.showAssignActionButton || this.$scope.showTagActionButton || this.$scope.showDeleteActionButton;
          this.$scope.deletableGoalsCount = this.$scope.goals?.reduce((count, goal) => (this.isGoalDeletable(goal) ? ++count : count), 0) || 0;
        })
        .catch((error) => this.uiErrorHandlingService.handleModal(error));
    };

    if (this.gridFiltersChange?.initial) {
      return this.apmSpanService.dataLoadSpan("allowed-actions", loadAllowedActions);
    } else {
      loadAllowedActions();
    }
  }

  public openGoalForm = (): void => {
    this.$state.go("._goalForm.form", { flowName: PiTrackingFlowNameEnum.CreateObjectiveFormOkrsEmptyState });
  };

  public customNumericFieldHasPrecision = (field: ICustomField): boolean => {
    return field.settings?.formatting?.precision !== undefined && field.settings.formatting.precision !== FULL_NUMBER_PRECISION;
  };

  private initFilterChangeActionsContent = (): void => {
    this.$scope.saveFilterChangesContent = [
      ...(this.$scope.isOkrViewDisplayed
        ? this.generateOkrViewFilterChangeActionsContent()
        : [
            {
              text: "You can save this as a custom OKR view",
            },
            {
              text: "Save as OKR view",
              ctaCb: () => this.openOkrViewCreationModal(),
            },
          ]),
    ];

    this.$scope.$evalAsync();
  };

  private generateOkrViewFilterChangeActionsContent = (): SaveFilterChangesContent => {
    return this.currentUserAllowedActionsMap.update
      ? [
          {
            text: "Save",
            ctaCb: () => this.patchOkrView(),
          },
          {
            text: "or",
          },
          {
            text: "Save as new OKR view",
            ctaCb: () => this.openOkrViewCreationModal(),
          },
        ]
      : [
          {
            text: "Save as new OKR view",
            ctaCb: () => this.openOkrViewCreationModal(),
          },
        ];
  };

  private generateShareableFilterPayload = (): ShareableFilterBasePostDTO => {
    return {
      filters: this.$scope.filters[0],
      sorting: this.$scope.sortingRules,
    };
  };

  private openOkrViewCreationModal = (): void => {
    const shareableFilter = this.generateShareableFilterPayload();

    this.shareableFiltersModalsService
      .openSaveFilterAsViewModal({ shareableFilter, placeholder: "Enter an OKR View name", hint: "What different users will see depend on their access to sessions." })
      .afterClose.pipe(take(1))
      .subscribe((data) => {
        if (!data) return;

        navigateToSingleOkrView(this.$state, data.id);
      });
  };

  private patchOkrView = (): void => {
    const shareableFilter = this.generateShareableFilterPayload();

    this.shareableFiltersFacade
      .patchShareableFilter$(this.$stateParams.okrViewId, shareableFilter)
      .pipe(take(1))
      .subscribe({
        next: (sharedFilterSettings) => {
          this.$scope.hasFilterOrSortingChanged = false;
          this.okrViewEventsManager.notifyOkrViewFilterChanged({
            filters: sharedFilterSettings.filters,
            sorting: sharedFilterSettings.sorting,
          });
          this.sharedFilterSettings = sharedFilterSettings;
          this.$scope.$evalAsync();
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  };

  private haveSessionsChanged(sessionIds: string[]): boolean {
    return this.$scope.allowedActionsMap?.size !== sessionIds.length || sessionIds.some((session) => !this.$scope.allowedActionsMap?.has(session));
  }

  private setColumnsAndSortingDefinitions = (): void => {
    // TODO: remove filter when all users settings are patched
    this.$scope.columns = getOkrGridColumns(goalsViewOptionsPath, this.$scope.customFields.goal || []).filter((col) => col.field !== "metricsCount");

    const visibleColumnsOnly = (column: IOkrGridColumn): boolean => column.visible;

    // assignments below can be done in 1 iteration, but columns count is limited and readability is better if done in multiple iterations
    // the map below always holds full set of available columns
    // on columns change this.$scope.columns will be missing all values with visible: false
    this.columnsFieldMap = toKeyMap("field", this.$scope.columns);
    this.$scope.columnDefinitions = toGridDefinitionMap(this.$scope.columns.map((c) => convertColumnToColumnDefinition(c, this.$filter)));
    this.$scope.gridColumns = this.$scope.columns.filter(visibleColumnsOnly).map((column) => ({ fieldName: column.field }));
    this.$scope.sortingDefinitions = this.createSortingDefinitionsMap();
  };

  private createSortingDefinitionsMap = () => {
    return toGridDefinitionMap(
      this.$scope.columns
        .filter((col) => col.field !== "krs" && col.field !== "krsAndChildObjs" && col.visible && col.isSortable)
        .map((c) => convertColumnToSortingDefinition(c, this.$filter))
        .concat({
          displayName: localize("number_of_key_results_cap"),
          fieldName: "metricsCount",
          trackingName: "Metrics count",
        })
    );
  };

  private catchError = (error): void => {
    if (this.$scope.indicators.loading) {
      this.$scope.indicators.loading = { error };
    } else {
      this.$scope.indicators.reloading = false;
      this.uiErrorHandlingService.handleModal(error);
    }
  };

  private initGridFilters = (): IPromise<void> => {
    if (this.shouldInitGridFiltersUsingPersistedSettings()) {
      this.initFilterSettings();
      this.initSortingSettings();
      if (this.$scope.isOkrViewsFeatureEnabled) {
        this.initFilterChangeActionsContent();
      }
      return this.$q.resolve();
    }

    return this.$q.all([this.getSharedFilterSettings(), this.getCurrentUserAllowedActions()]).then(([sharedFilterSettings, actions]) => {
      this.sharedFilterSettings = sharedFilterSettings;
      this.currentUserAllowedActionsMap = actions.currentUserActions.reduce((acc, action) => ((acc[action] = true), acc), {});
      if (this.$scope.isOkrViewsFeatureEnabled) {
        this.initFilterChangeActionsContent();
      }
      this.initOkrViewEventSubscriptions();

      this.initFilterSettings();
      this.initSortingSettings();
    });
  };

  private initOkrViewEventSubscriptions = (): void => {
    if (!this.$scope.isOkrViewDisplayed || this.okrViewEventsManager) return;

    this.okrViewEventsManager = new OkrViewEventsManager(this.$stateParams.okrViewId);
    this.okrViewEventsManager
      .okrViewAccessChanged$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((access) => {
        this.sharedFilterSettings.access = access;
      });
  };

  private shouldInitGridFiltersUsingPersistedSettings = (): boolean => {
    return !this.shouldLoadFiltersFromTransientFilter() && !this.shouldLoadFiltersFromPermanentFilter();
  };

  private shouldLoadFiltersFromTransientFilter = (): boolean => {
    return this.$scope.isShareableLinksWithAppliedFiltersFeatureEnabled && !!this.$stateParams.filterId;
  };

  private shouldLoadFiltersFromPermanentFilter = (): boolean => {
    return this.$scope.isOkrViewsFeatureEnabled && !!this.$stateParams.okrViewId;
  };

  private initFilterSettings = (): void => {
    if (!this.shouldInitGridFiltersUsingPersistedSettings()) {
      this.initFiltersWithValue([this.sharedFilterSettings.filters]);
      return;
    }

    const persistedFilters = this.toValidPersistedFilterSettings(this.getPersistedFilterSettings());
    this.initFiltersWithValue(persistedFilters);
  };

  private initSortingSettings = (): void => {
    if (!this.shouldInitGridFiltersUsingPersistedSettings()) {
      this.initSortingWithValue(this.sharedFilterSettings.sorting);
      return;
    }

    const persistedSorting = this.toValidPersistedSortingSettings(this.getPersistedSortingSettings());
    this.initSortingWithValue(persistedSorting);
  };

  private getPersistedFilterSettings = (): OrFiltersGroup[] => {
    return getPersistedScreenSettings(this.screenFiltersPath(), "filters") || [];
  };

  private getPersistedSortingSettings = (): IGridSortingRule[] => {
    return getPersistedScreenSettings(this.screenSortingPath(), "sorting") || [];
  };

  private toValidPersistedFilterSettings = (filters: OrFiltersGroup[]): OrFiltersGroup[] => {
    let persistedFilters: OrFiltersGroup[] = filters.map(
      (filtersGroup) =>
        (filtersGroup = filtersGroup.map((filter) =>
          filter.fieldName === "ownerId" ? { ...filter, fieldName: "ownerIds", operator: "contains" as GridFilterOperator } : filter
        ))
    );

    if (!this.$scope.isModuleTagsEnabled) {
      delete this.$scope.filterDefinitions["tags.title"];
      persistedFilters = removeFiltersByFieldNameAndEmptyGroups(persistedFilters, "tags.title");
    }

    return persistedFilters;
  };

  private toValidPersistedSortingSettings = (sorting: IGridSortingRule[]): IGridSortingRule[] => {
    return sorting.filter((sortingRule) => sortingRule.fieldName in this.$scope.sortingDefinitions);
  };

  private initFiltersWithValue = (filterSettings: OrFiltersGroup[]): void => {
    if (filterSettings.length) {
      this.$scope.filters = filterSettings;
    } else {
      this.$scope.filters = this.getDefaultFilterSettings();
    }

    if (this.$scope.isOkrFiltersImprovementEnabled && !this.$scope.isOkrViewDisplayed) {
      this.$scope.filtersUpdated(this.$scope.filters);
    }
  };

  private initSortingWithValue = (sortingSettings: IGridSortingRule[]): void => {
    if (sortingSettings.length) {
      this.$scope.sortingRules = sortingSettings;
    } else {
      this.$scope.sortingRules = this.getDefaultSortingSettings();
    }
  };

  private getDefaultFilterSettings = (): OrFiltersGroup[] => {
    const defaultFilters = generateDefaultFilters(this.$stateParams.planningSessionId);
    return this.$scope.isModuleTagsEnabled ? defaultFilters : removeFiltersByFieldNameAndEmptyGroups(defaultFilters, "tags.title");
  };

  private getDefaultSortingSettings = (): IGridSortingRule[] => {
    // TODO: discuss with UX whether to create sorting definitions for invisible columns
    const attainmentColumnSelected = this.$scope.gridColumns.find((column) => column.fieldName === "attainment");
    return attainmentColumnSelected ? [{ fieldName: "attainment", reversed: false }] : [];
  };

  private screenFiltersPath = () => goalsGridPersistedScreenPath(this.$stateParams.planningSessionId);

  private screenSortingPath = () => goalsPersistedScreenPath(this.$stateParams.planningSessionId, gridScreenName);

  private getCurrentUserAllowedActions(): IPromise<IUserItemAllowedActions> {
    return this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$stateParams.okrViewId, "shareableFilter");
  }

  private getSharedFilterSettings = (): IPromise<ShareableFilter> => {
    const shareableFilterDefer = this.$q.defer<ShareableFilter>();
    const filterId = this.$scope.isOkrViewDisplayed ? this.$stateParams.okrViewId : this.$stateParams.filterId;

    this.shareableFiltersFacade
      .getShareableFilterById$(filterId)
      .pipe(take(1))
      .subscribe({
        next: (shareableFilter) => {
          shareableFilterDefer.resolve(shareableFilter);
        },
        error: () => shareableFilterDefer.resolve({ id: "", name: "", filters: [], sorting: [] }),
      });

    return shareableFilterDefer.promise;
  };

  private handleShareableLinkCopiedFromSubNavigation = (event, item: INavItem): void => {
    const allShareableFiltersDisabled = !this.$scope.isShareableLinksWithAppliedFiltersFeatureEnabled && !this.$scope.isOkrViewsFeatureEnabled;
    const typeNotSupported = item.uxcType !== "session" && item.uxcType !== "filter";
    if (typeNotSupported || allShareableFiltersDisabled) return;

    this.copyShareableLinkWithAppliedFilters();
  };

  public openQuantivePlusWizard = (): void => {
    this.ng1UiModalService.create<GuidedOKRsComponent, GuidedOKRsModalData>({
      uiContent: GuidedOKRsComponent,
      uiWrapClassName: "guided-okrs-modal-wrapper",
      uiFooter: null,
      uiClosable: false,
      uiKeyboard: false,
      uiData: {
        flowName: PiTrackingFlowNameEnum.GuidedOkrsCreationCreateOkrTopNav,
        currentSession: this.$scope.allSessions.find((s) => s.id === this.$scope.session.id),
        readOnlySession: true,
      },
    });
  };
  private copyShareableLinkWithAppliedFilters = () => {
    if (this.$scope.isOkrViewDisplayed) {
      this.copyShareableLinkWithAppliedPermanentFilters();
      return;
    }

    this.copyShareableLinkWithAppliedTransientFilters();
  };

  private copyShareableLinkWithAppliedPermanentFilters = (): void => {
    if (determineAccessType(this.sharedFilterSettings.access) === SimplePermissionsAccessType.private) {
      this.shareableFiltersFacade.notifyFilterAccessRestricted("Edit who has access to make this OKR view visible to others");
      this.okrViewEventsManager.notifyOkrViewPrivateAccessChangeRequested();

      return;
    }

    const currentState = this.$uiRouterGlobals.current.name;
    this.shareableFiltersFacade.copyShareableOkrViewLink(currentState, { okrViewIs: this.$stateParams.okrViewId });
  };

  private copyShareableLinkWithAppliedTransientFilters = (): void => {
    const currentState = this.$uiRouterGlobals.current.name;
    const shareableFilter: ShareableFilterTransientPostDTO = {
      filters: this.$scope.filters[0],
      sorting: this.$scope.sortingRules,
      transient: true,
    };

    this.shareableFiltersFacade.copyShareableLinkWithAppliedFilterIdQuery(shareableFilter, currentState, { planningSessionId: this.$stateParams.planningSessionId });
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    this.$rootScope.traceAction("change_filters", () => {
      this.gridFiltersChange = change;
      this.$scope.filterTagSelection = getCommonValuesUsedInAllFiltersInEveryGroup(this.$scope.filters, { fieldName: filterFieldNames.tags });
      const displayedSessionIds = this.getDisplayedSessionIds();
      if (this.haveSessionsChanged(displayedSessionIds)) {
        this.loadAllowedActionsMap(displayedSessionIds);
      }

      if (change.initial) return;

      this.$scope.hasFilterOrSortingChanged = true;

      if (this.$scope.session && this.$scope.session.id !== MISSING_SESSION_ID_STATE_PARAM) {
        // filters are shared between goals views
        // when changing filters in an existing session the expanded collection should be wiped so tree views should look as expected when visited
        resetPersistedExpandedItems({ entityId: this.$scope.session.id, screenPath: expandedGoalsScreenPath(this.$scope.session.id) });
      }

      if (!this.$scope.isOkrViewDisplayed) {
        setPersistedScreenSettings(this.screenFiltersPath(), "filters", this.$scope.filters);

        if (this.$scope.isOkrFiltersImprovementEnabled) {
          this.$scope.filtersUpdated(this.$scope.filters);
        }
      }

      this.setCurrentPage(FIRST_PAGE);

      if (this.$scope.goalsBulkAction === "delete") {
        this.resetGoalsIdsToBulkDelete();
      }
    });
  };

  private sortingChange = (): void => {
    this.$rootScope.traceAction("change_sorting", () => {
      setPersistedScreenSettings(this.screenSortingPath(), "sorting", this.$scope.sortingRules);
      this.$scope.hasFilterOrSortingChanged = true;
      this.setCurrentPage(FIRST_PAGE);
    });
  };

  private columnsChange = (): void => {
    this.$rootScope.traceAction("change_columns", () => {
      this.$scope.columns = this.$scope.gridColumns.map((gridColumn) => ({
        ...this.columnsFieldMap[gridColumn.fieldName],
        visible: true,
      }));
      const persistedSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};
      persistedSharedViewOptions.okrGridColumns = this.$scope.columns;
      setPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions", persistedSharedViewOptions);
      this.$scope.sortingDefinitions = this.createSortingDefinitionsMap();

      if (this.areSortingRuleMissingFromSortingDefinitions()) {
        this.initSortingSettings();
        this.sortingChange();
      }
      this.$scope.$digest();
    });
  };

  private areSortingRuleMissingFromSortingDefinitions = (): boolean => {
    const rulesNotInDefinitions = this.$scope.sortingRules.filter((sortingRule) => !(sortingRule.fieldName in this.$scope.sortingDefinitions));
    return rulesNotInDefinitions.length > 0;
  };

  private setTotalPages = (totalCount: number) => {
    this.$scope.totalPages = Math.ceil(totalCount / PAGING_SIZE);

    if (this.$scope.totalPages && this.$scope.totalPages < this.$scope.currentPage) {
      this.setCurrentPage(this.$scope.totalPages);
    }
  };

  private setCurrentPage = (page?: number) => {
    const currentPage = page || FIRST_PAGE;
    this.changePageOrReloadGoals(currentPage);
  };

  private onPageChange = (page: number) => {
    this.$rootScope.traceAction("change_page", () => {
      this.changePageOrReloadGoals(page);

      if (document.getElementById("goals-wrap")) {
        scrollToTop("#goals-wrap");
      }
    });
  };

  private changePageOrReloadGoals(page: number) {
    if (page !== this.$scope.currentPage || (this.$uiRouterGlobals.params.page && page !== Number(this.$uiRouterGlobals.params.page))) {
      this.$state.go(".", { page: page });
    } else {
      this.$scope.currentPage = page;
      this.getGoals();
    }
  }

  private watchForPageChanges = () => {
    const unsubsrcribePageChange = this.$transitions.onSuccess({ to: "gtmhub.**.all" }, (transition: Transition) => {
      const page = Number(transition.paramsChanged().page) || Number(transition.params().page) || FIRST_PAGE;
      if (page !== this.$scope.currentPage) {
        this.$scope.currentPage = page;
        this.getGoals();
      }
    });
    this.$scope.$on("$destroy", unsubsrcribePageChange);
  };

  private filterByTag = (tag: string): void => {
    this.$rootScope.traceAction("filter_by_tag", () => {
      const args = { fieldName: filterFieldNames.tags, value: tag, createFilter: this.createTagFilter };
      this.$scope.filters = this.$scope.filterTagSelection.has(tag)
        ? removeValueFromFiltersInEveryGroup(this.$scope.filters, args)
        : addValueToExistingFiltersOrCreateNewFilterInEveryGroup(this.$scope.filters, args);

      this.trackFiltersChangedExternally({
        source: "OKR Tag Label",
        fieldName: filterFieldNames.tags,
        isAdding: !this.$scope.filterTagSelection.has(tag),
      });
    });
  };

  private createTagFilter = (value: string[]): IArrayGridFilter => ({ ...(<IArrayGridFilter>createFilter(this.$scope.filterDefinitions[filterFieldNames.tags])), value });

  private trackFiltersChangedExternally = ({ source, fieldName, isAdding }: { source: string; fieldName: string; isAdding: boolean }) => {
    this.analyticsService.track("Filter Changed Externally", {
      source: source,
      field_name: fieldName,
      is_adding: isAdding,
    });
  };

  private tag = (item: IAllGoalTag, subject: IAllGoal) => {
    this.$rootScope.traceAction("tag_goal", () => {
      const tagAlreadyAdded = subject.tags.some((goalTag) => goalTag.title === item.title);
      if (tagAlreadyAdded) {
        return;
      }

      this.goalService.addTagToGoal(item.title, subject.id).then(
        () => {
          this.getGoals();
        },
        (err) => this.uiErrorHandlingService.handleModal(err)
      );
    });
  };

  private removeTag = (goal: IAllGoal, tag: IAllGoalTag) => {
    this.$rootScope.traceAction("remove_tag_from_goal", () => {
      this.goalService.removeTagFromGoal(tag, goal.id);

      this.$scope.indicators.reloading = true;

      this.$timeout(() => {
        this.getGoals();
      }, 400);
    });
  };

  private getGoals = (): IPromise<unknown> => {
    const initGoals = () => this.initializeGoals().catch((error) => this.catchError(error));
    if (this.isFirstLoad) {
      return this.apmSpanService.dataLoadSpan("goals", initGoals);
    } else {
      initGoals();
    }
  };

  private initializeGoals = () => {
    const skip = (this.$scope.currentPage - 1) * PAGING_SIZE;
    const goalsRequest = this.initializeGoalsRequest();

    goalsRequest.skip = skip;
    goalsRequest.limit = PAGING_SIZE;
    goalsRequest.fields =
      `metrics{id,confidence,name,attainment,ownerIds,manualType,lastCheckInDate,cascadeType,links,orderId},` +
      `parentId,ownerIds,description,name,dateFrom,dateTo,childrenCount,fullSubTreeCount,metricsCount,` +
      "attainment,fullAggregatedAttainment,sessionId,customFields,obfuscated,private,workflow{status},locked," +
      "tags{id,name,title,isActive,itemsTaggedCount},attainmentTypeString,designScore{totalPoints}";

    this.$scope.indicators.reloading = true;

    return this.onGetGoalsV2(goalsRequest);
  };

  private initializeGoalsRequest = (): IRestLayerRequest => {
    const goalsWithoutSessionAreDisplayed = this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM;

    // copying filters as we have to modify values passed to generate rest layer request but we don't want to modify component values
    let filters = angular.copy(this.$scope.filters);
    let filtersOverride: Record<string, RestLayerFilterFactory> = null;
    // to do: remove this as well as related variables and util methods when we implement non-existent dynamic filter
    // https://gtmhub.atlassian.net/browse/GVS-2095
    if (goalsWithoutSessionAreDisplayed) {
      filters = filters.map((filtersGroup) => processedFiltersForSessionlessGoals(filtersGroup));
      filtersOverride = filtersOverrideForSessionlessGoals();
    }

    filtersOverride = { ...filtersOverride, ...filtersOverrideForTagTitle(), ...filtersOverrideForOkrName() };

    const separatedFilterValues: OrFiltersGroup[] = separateDynamicValuesForAllGroups(filters);
    return toRestLayerRequest({ filtersOverride, filters: separatedFilterValues, sorting: this.$scope.sortingRules });
  };

  private onGetGoalsV2 = (goalsRequest: IRestLayerRequest) => {
    return this.goalService.getGoalsV2<IAllGoal>(goalsRequest).then((result) => {
      this.goalsTotalCount = result.totalCount;
      this.setTotalPages(this.goalsTotalCount);

      delete this.$scope.indicators.loading;
      this.$scope.indicators.reloading = false;
      this.$scope.goals = result.items || [];
      this.$scope.ownersMap = this.$scope.goals.reduce<Record<string, string[]>>((map, goal) => {
        map[goal.id] = getDisplayableOwnerIds(goal, "goal", this.$scope.methodologySettings);
        return map;
      }, {});

      if (this.$scope.goalsBulkAction === "delete") {
        this.$scope.goalsIdsToBulkDelete.count = result.items.reduce((count, goal) => {
          return this.$scope.goalsIdsToBulkDelete.items.indexOf(goal.id) !== -1 ? count + 1 : count;
        }, 0);
      }

      this.setEmptyScreenAndNoFilterDisplay();

      this.getGoalsChildren();

      this.$scope.deletableGoalsCount = this.$scope.goals?.reduce((count, goal) => (this.isGoalDeletable(goal) ? ++count : count), 0) || 0;
    });
  };

  private setEmptyScreenAndNoFilterDisplay = (): void => {
    const anyFilterWithValuesInGroups = hasFiltersGroupsWithValues(this.$scope.filters);
    const onlyHostSessionFilterInAllGroups = hasOnlyHostSessionFilterInAllGroups(this.$scope.filters, this.$stateParams.planningSessionId);

    this.$scope.showEmptyBlock = this.noGoalsFetched() && (!anyFilterWithValuesInGroups || onlyHostSessionFilterInAllGroups);
    this.$scope.showNoFilterResults = this.noGoalsFetched() && anyFilterWithValuesInGroups && !onlyHostSessionFilterInAllGroups;
  };

  private noGoalsFetched = (): boolean => !this.$scope.indicators.loading && !this.$scope.indicators.reloading && this.goalsTotalCount === 0;

  private getGoalsChildren = (): void => {
    const goalsChildrenRequestParams = this.buildGoalsChildrenQuery();

    if (goalsChildrenRequestParams) {
      this.goalService.getGoalsV2<IAllGoal>(goalsChildrenRequestParams).then((result) => {
        this.$scope.parentChildrenIdMap = buildParentChildrenIdMap(this.$scope.goals, result.items);
      });
      return;
    }

    this.$scope.parentChildrenIdMap = buildParentChildrenIdMap(this.$scope.goals);
  };

  private buildGoalsChildrenQuery = (): IRestLayerRequest => {
    const parentGoalsIds = this.$scope.goals.filter((goal) => goal.childrenCount > 0 && goal.attainmentTypeString !== "average_kr").map((goal) => goal.id);

    if (!parentGoalsIds.length) {
      return;
    }

    const filters: RestLayerFilter[] = [];
    filters.push({ parentId: { $in: parentGoalsIds } });

    const sessionIdsInFilters = this.$scope.filters.reduce((sessionIds: string[], filtersGroup: OrFiltersGroup): string[] => {
      const sessionFilter = filtersGroup.find((f) => f.fieldName === "sessionId");
      if (sessionFilter && sessionFilter.value) {
        sessionIds.push(...(<IArrayGridFilter>sessionFilter).value);
      }
      return sessionIds;
    }, []);

    if (sessionIdsInFilters.length) {
      filters.push({ sessionId: { $in: [...new Set(sessionIdsInFilters)] } });
    }

    return {
      filter: { $and: filters },
      // we need to take all children goals
      page: FIRST_PAGE,
      limit: -1,
      fields: `parentId,ownerIds,name,attainment,sessionId,obfuscated,orderId`,
    };
  };

  private openGoal = (id: string) => {
    this.$state.go("._goalSidebar.goal", { id: id });
  };

  private deleteGoal = (goalId: string) => {
    this.$rootScope.traceAction("delete_goal", () => {
      this.$scope.indicators.reloading = true;
      this.goalService.deleteGoal(goalId).then(
        () => {
          this.getGoals();
        },
        (err) => {
          this.$scope.indicators.reloading = false;
          this.uiErrorHandlingService.handleModal(err);
        }
      );
    });
  };

  private onGoalDeleted = (evt, id: string): void => {
    for (let i = 0; i < this.$scope.goals.length; i++) {
      if (this.$scope.goals[i].id === id) {
        this.$scope.goals.splice(i, 1);
      }
    }

    if (this.$scope.parentChildrenIdMap[id]) {
      delete this.$scope.parentChildrenIdMap[id];
    }

    Object.entries(this.$scope.parentChildrenIdMap).forEach(([parentId, children]) => {
      this.$scope.parentChildrenIdMap[parentId] = children.filter((child) => child.id !== id);
    });
  };

  private onGoalsChanged = () => {
    if (this.$scope.session && this.$scope.session.id) {
      this.getGoals();
    }
  };

  private onMetricCreated = (detail: IMetricChangedEventArgs["detail"]): void => {
    const goal = this.$scope.goals.find((goal) => goal.id === detail.goalId);

    if (!goal) return;

    if (!goal.metrics) {
      goal.metrics = [detail.metric];
    } else {
      goal.metrics.push(detail.metric);
    }

    this.$scope.parentChildrenIdMap = buildParentChildrenIdMap(this.$scope.goals);
  };

  private updateGoalsTree = (evt, wsData: IGoalsMetricsData) => {
    if (this.$stateParams.planningSessionId === wsData.sessionId) {
      this.goalsTreeService.updateGoalsTree(wsData, this.$scope.goals);
    }
  };

  private deleteMetric = (goalId: string, metricId: string) => {
    this.$rootScope.traceAction("delete_metric", () => {
      this.metricService.deleteMetric(goalId, metricId).then(() => {
        this.trackMetricMilestoneDelete(goalId, metricId);
        this.getGoals();
      });
    });
  };

  private trackMetricMilestoneDelete = (goalId: string, metricId: string): void => {
    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.MetricMilestone)
      .pipe(first())
      .subscribe((isFeatureEnabled) => {
        if (isFeatureEnabled) {
          const metric = this.$scope.goals?.find((goal) => goal.id === goalId)?.metrics?.find((metric) => metric.id === metricId);

          if (!metric) return;

          this.metricService.trackMetricMilestone(MetricMilestoneEventType.KR_MILESTONES_DELETED, { metric });
        }
      });
  };

  private deleteContributor = (contributor: OkrGridContributor, goal: IAllGoal): void => {
    if (contributor.childType === "metric") {
      this.deleteMetric(goal.id, contributor.id);
      return;
    }

    this.deleteGoal(contributor.id);
  };

  private toggleGoalIdToBulkDelete = (goalId: string): void => {
    if (this.$scope.goalsIdsToBulkDelete.items.indexOf(goalId) > -1) {
      this.removeGoalIdToBulkDelete(goalId);
    } else {
      this.addGoalIdToBulkDelete(goalId);
    }

    // the current method is triggered by an a12 component event which doesn't trigger digest automatically
    this.$scope.$evalAsync();
  };

  private toggleAllGoalsIdsToBulkDelete = (args: { checked: boolean }): void => {
    const method = args.checked ? "addGoalIdToBulkDelete" : "removeGoalIdToBulkDelete";
    this.$scope.goals.forEach((goal) => this.isGoalDeletable(goal) && this[method](goal.id));

    // the current method is triggered by an a12 component event which doesn't trigger digest automatically
    this.$scope.$evalAsync();
  };

  private handleGoalsDeleted = (): void => {
    this.resetGoalsIdsToBulkDelete();
    this.onPageChange(FIRST_PAGE);
  };

  private checkMethodologySettings = (): void => {
    this.$scope.methodologySettings = {
      areMultipleObjectiveOwnersDisabled: this.okrMethodologyFacade.areMultipleOwnersDisabled("goal", this.$scope.session?.settings),
      areMultipleKrOwnersDisabled: this.okrMethodologyFacade.areMultipleOwnersDisabled("metric", this.$scope.session?.settings),
    };
  };

  private setDefaultMethodologySettings = () => {
    this.$scope.methodologySettings = generateDefaultOkrMethodologySettings();
  };

  private changeAssignee = (item: Assignee, subject: IAllGoal | IAllMetric, subjectType: string) => {
    this.checkMethodologySettings();

    this.$rootScope.traceAction("change_assignee", () => {
      if (subject.ownerIds && subject.ownerIds.includes(item.id)) return;
      let ownerPatch = { ownerIds: [...subject.ownerIds, item.id] };

      if (subjectType === "goal") {
        if (this.$scope.methodologySettings.areMultipleObjectiveOwnersDisabled) {
          ownerPatch = { ownerIds: [item.id] };
        }
        this.goalService.patchGoal(subject.id, ownerPatch).then(
          () => {
            this.getGoals();
          },
          (err) => this.uiErrorHandlingService.handleModal(err)
        );
      }

      if (subjectType === "metric") {
        if (this.$scope.methodologySettings.areMultipleKrOwnersDisabled) {
          ownerPatch = { ownerIds: [item.id] };
        }

        const patch = {
          id: subject.id,
          ...ownerPatch,
        };

        this.metricService.patchMetric(patch).then(
          () => {
            this.getGoals();
          },
          (err) => this.uiErrorHandlingService.handleModal(err)
        );
      }
    });
  };

  private onTeamsSelected = (params: ITeamsSelectedEventArgs) => {
    if (params.isSingleTeam) {
      this.$timeout(() => {
        this.$state.go("gtmhub.goals.all", { planningSessionId: this.$scope.session.id }, { reload: true });
      }, 100);
    }
  };

  private exportToExcel = (): void => {
    const filtersOverride = { ...filtersOverrideForTagTitle(), ...filtersOverrideForOkrName() };
    const { filter, sort } = toRestLayerRequest({
      filters: this.$scope.filters,
      sorting: this.$scope.sortingRules,
      filtersOverride: filtersOverride,
    });
    const hostSessionId = this.$scope.session.id;

    this.documentExporterService.exportGoalsGridToExcel({ filter, sort, hostSessionId }).then(
      (response) => {
        const generatedExcel = new Blob([response.data], { type: "application/octet-stream" });
        const fileName = response.headers("File-Name");
        triggerFileDownload(generatedExcel, fileName);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private isProgressAggregationDisabledForSession = (sessionId: string): boolean => {
    const sessionSettings = this.$scope.sessionsIdMap[sessionId]?.settings;
    return this.okrMethodologyFacade.isProgressAggregationDisabled(sessionSettings);
  };

  private toggleGoalsBulkAction = (action: IAllGoalsScope["goalsBulkAction"]): void => {
    if (this.$scope.goalsBulkAction === action) {
      this.$scope.goalsBulkAction = null;
      this.resetGoalsIdsToBulkDelete();

      this.$scope.$evalAsync();
    } else {
      this.$scope.goalsBulkAction = action;
    }
  };

  private addGoalIdToBulkDelete(goalId: string): void {
    if (this.$scope.goalsIdsToBulkDelete.items.indexOf(goalId) > -1) {
      return;
    }
    this.$scope.goalsIdsToBulkDelete.items = [...this.$scope.goalsIdsToBulkDelete.items, goalId];
    this.$scope.goalsIdsToBulkDelete.count++;
  }

  private removeGoalIdToBulkDelete(goalId: string): void {
    const goalIdIndex = this.$scope.goalsIdsToBulkDelete.items.indexOf(goalId);
    if (goalIdIndex < 0) {
      return;
    }
    this.$scope.goalsIdsToBulkDelete.items.splice(goalIdIndex, 1);
    this.$scope.goalsIdsToBulkDelete.items = [...this.$scope.goalsIdsToBulkDelete.items];
    this.$scope.goalsIdsToBulkDelete.count--;
  }

  private resetGoalsIdsToBulkDelete(): void {
    this.$scope.goalsIdsToBulkDelete = { items: [], count: 0 };
  }

  private setSessionData(): void {
    if (this.$stateParams.planningSessionId !== MISSING_SESSION_ID_STATE_PARAM) {
      this.apmSpanService.dataLoadSpan("sessions", () =>
        this.$scope
          .watchUntilAndRejectOnError(
            () => this.$scope.sessionsFetched,
            () => this.$scope.sessionsError
          )
          .then(
            () => {
              this.$scope.session = this.$scope.sessionsIdMap[this.$stateParams.planningSessionId] || null;
              this.$scope.workflowEnabled = this.$scope.session?.workflow?.reviewers > 0;

              if (this.$scope.session) {
                this.sessionEvents.broadcastSessionVisited(this.$scope.session);
              }
            },
            (error) => this.uiErrorHandlingService.handleModal(error)
          )
      );
    }
  }

  private isGoalDeletable = ({ sessionId }: IGoal): boolean => {
    // sessionless goals should always be deletable (permissions are per session)
    return !sessionId || (this.$scope.allowedActionsMap?.get(sessionId)?.has("delete") && this.$scope.sessionsIdMap?.[sessionId]?.status === "open");
  };

  private getDisplayedSessionIds(): string[] {
    const filteredSessionIds = extractArrayFilterValues(this.$scope.filters, "sessionId");
    const existingSessionIds = Object.keys(this.$scope.sessionsIdMap);
    const existingFilteredSessionIds = filteredSessionIds.filter((sessionId) => existingSessionIds.includes(sessionId));

    // if no session filter is applied, all sessions are currently displayed
    const sessionIds = existingFilteredSessionIds?.length > 0 ? existingFilteredSessionIds : existingSessionIds;

    // clear duplicates in case of activeSessions filter present
    return Array.from(new Set(sessionIds));
  }
}
